import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, MessagesSquare, Loader2, Send, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, timeAgo } from "@/lib/api";
import { useAuth, useRequireAuth } from "@/context/AuthContext";
import { VerifiedProfessionalBadge } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function CommunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const [c, setC] = useState(null);
  const [posts, setPosts] = useState(null);
  const [members, setMembers] = useState(null);
  const [tab, setTab] = useState("discussion");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadCommunity = useCallback(() => api.get(`/communities/${id}`).then(({ data }) => setC(data)).catch(() => navigate("/communities")), [id, navigate]);
  const loadPosts = useCallback(() => api.get(`/communities/${id}/posts`).then(({ data }) => setPosts(data)), [id]);
  const loadMembers = useCallback(() => api.get(`/communities/${id}/members`).then(({ data }) => setMembers(data)), [id]);

  useEffect(() => { loadCommunity(); loadPosts(); loadMembers(); }, [loadCommunity, loadPosts, loadMembers]);

  const join = () => requireAuth(async () => {
    const { data } = await api.post(`/communities/${id}/join`);
    setC((p) => ({ ...p, joined: data.joined, member_count: data.member_count }));
    loadMembers();
    toast.success(data.joined ? "Joined community" : "Left community");
  });

  const post = () => requireAuth(async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/communities/${id}/posts`, { body: text });
      setPosts((p) => [data, ...(p || [])]);
      setText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    } finally { setBusy(false); }
  });

  if (!c) return <div className="p-4"><Skeleton className="h-48 w-full rounded-2xl" /></div>;
  const canPost = user && (user.role === "admin" || c.joined);

  return (
    <div className="pb-8" data-testid="community-detail-page">
      <div className="relative">
        {c.image_url ? <img src={fileUrl(c.image_url)} alt="" className="h-36 w-full object-cover" /> : <div className="h-24 w-full bg-navy navy-grid" />}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-navy/10" />
        <button onClick={() => navigate("/communities")} className="absolute left-3 top-3 rounded-full bg-white/90 p-2 text-navy shadow"><ArrowLeft className="h-4 w-4" /></button>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          {c.category && <p className="text-[11px] font-semibold uppercase tracking-wide text-medorange">{c.category}</p>}
          <h1 className="font-heading text-xl font-extrabold">{c.name}</h1>
        </div>
      </div>

      <div className="px-4 py-3">
        <p className="text-sm text-slate-600">{c.description}</p>
        <div className="mt-3 flex items-center gap-3">
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600"><Users className="h-3.5 w-3.5" /> {c.member_count} members</span>
          <Button onClick={join} data-testid="community-detail-join-button"
            className={c.joined ? "ml-auto bg-slate-100 text-slate-700 hover:bg-slate-200" : "ml-auto bg-medorange text-white hover:bg-medorange-hover"}>
            {c.joined ? "Joined" : "Join Community"}
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="px-4">
        <TabsList className="w-full bg-slate-100">
          <TabsTrigger value="discussion" data-testid="community-tab-discussion" className="flex-1 gap-1.5 data-[state=active]:text-medorange"><MessagesSquare className="h-4 w-4" /> Discussion</TabsTrigger>
          <TabsTrigger value="members" data-testid="community-tab-members" className="flex-1 gap-1.5 data-[state=active]:text-medorange"><Users className="h-4 w-4" /> Members</TabsTrigger>
        </TabsList>

        <TabsContent value="discussion" className="mt-3 space-y-3">
          {canPost ? (
            <div className="flex items-end gap-2">
              <Textarea data-testid="community-post-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Share with the community…" className="min-h-[44px] resize-none" />
              <Button data-testid="community-post-submit" onClick={post} disabled={busy} className="gap-1 bg-medorange hover:bg-medorange-hover">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-500">
              <Lock className="h-4 w-4 text-slate-400" /> Join this community to post in the discussion.
            </div>
          )}
          {posts === null && <Skeleton className="h-20 w-full rounded-xl" />}
          {posts && posts.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No discussion yet. Start the conversation.</p>}
          {posts && posts.map((p) => {
            const initials = (p.author_name || "M").split(" ").map((w) => w[0]).slice(0, 2).join("");
            return (
              <div key={p.id} className="ml-rise rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7"><AvatarFallback className="bg-navy text-[10px] font-bold text-white">{initials}</AvatarFallback></Avatar>
                  <p className="text-xs font-bold text-slate-800">{p.author_name}</p>
                  {p.author_verified_professional && <VerifiedProfessionalBadge compact />}
                  <span className="text-[11px] text-slate-400">· {timeAgo(p.created_at)}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600">{p.body}</p>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="members" className="mt-3 space-y-2">
          {members === null && <Skeleton className="h-16 w-full rounded-xl" />}
          {members && members.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No members yet. Be the first to join.</p>}
          {members && members.map((m) => {
            const initials = (m.name || "M").split(" ").map((w) => w[0]).slice(0, 2).join("");
            return (
              <div key={m.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-medorange text-xs font-bold text-white">{initials}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-bold text-slate-900">{m.name}</p>
                    {m.professional_verified && <VerifiedProfessionalBadge compact />}
                  </div>
                  <p className="truncate text-xs text-slate-500">{m.specialty || "Member"}</p>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
